/**
 * WidgetToolbar — the FLOATING widget palette (TV1-2291 · S18).
 *
 * `CollapsedBar` (the docked strip of icons) + `PaletteDrawer` (the full-screen
 * picker it opens). **This pair stays** — it is the interaction the ticket owner
 * asked to keep. S18 fixes it; it does not replace it.
 *
 * What was fixed:
 *
 *  - **The dead tabs are gone.** Grouping ran through
 *    `categoryOf = w => w.includes( 'Chart' ) ? 'Charts' : 'All Widgets'` over a
 *    four-member `Category` union, so `Favorites` and `Tables` could NEVER receive
 *    a member — two tabs that had been shipping empty. Grouping is now by DATA
 *    SOURCE, derived from `widgetCatalog`, and `groupWidgetTypesBySource` drops
 *    empty groups, so a dead group is structurally impossible.
 *  - **Search stopped being developer-only.** It matched the raw type string, so
 *    typing "dona" found nothing while "donut" found the donut chart. It now
 *    matches the translated label and description too.
 *
 * This component is the CONTAINER: it owns open/filter/search state and derives the
 * groups. `PaletteDrawer` is the presenter and receives them already filtered.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { WidgetDefinition, WidgetType } from '../../Widgets/types';
import {
  groupWidgetTypesBySource,
  widgetDescriptionFallback,
  widgetDescriptionKey,
  widgetLabelFallback,
  widgetLabelKey,
  type WidgetDataSource,
  type WidgetSourceGroup
} from '../../widgetCatalog';
import CollapsedBar from './CollapsedBar/CollapsedBar';
import PaletteDrawer from './PaletteDrawer/PaletteDrawer';

/**
 * The drawer's active filter: one data source, or every source at once.
 *
 * Replaces the old `Category` union. `'all'` is not a group — it is the absence of
 * a filter, which is why it is a separate member here rather than a fourth source.
 */
export type WidgetPaletteFilter = WidgetDataSource | 'all';

type Props = {
  available: WidgetType[];
  onAddWidget: ( widget: Partial<WidgetDefinition> ) => void;
};

/**
 * Does a type match the search box?
 *
 * Matches the TRANSLATED label and description, not just the type string — the old
 * palette only matched the latter, so typing "dona" found nothing while "donut"
 * found the donut chart. The type string stays in the haystack because it is what a
 * developer or a support conversation will type, and keeping it costs nothing.
 *
 * Module-level (rather than a closure over `t`) so the `useMemo` below has literal
 * dependencies the React Compiler can verify.
 */
const matchesSearch = (
  type: WidgetType,
  query: string,
  t: ( key: string, fallback: string ) => string
): boolean => {
  if ( !query ) return true;
  const haystack = [
    type,
    t( widgetLabelKey( type ), widgetLabelFallback( type )),
    t( widgetDescriptionKey( type ), widgetDescriptionFallback( type ))
  ].join( ' ' ).toLowerCase();
  return haystack.includes( query );
};

export default function WidgetToolbar({ available, onAddWidget }: Props ) {
  const { t } = useTranslation( 'widgets' );
  const [ opened, setOpened ] = useState( false );
  const [ filter, setFilter ] = useState<WidgetPaletteFilter>( 'all' );
  const [ search, setSearch ] = useState( '' );

  // Derived from what the host ACTUALLY offers — never a hard-coded tab list.
  const availableGroups = useMemo(
    () => groupWidgetTypesBySource( available ),
    [available]
  );

  /*
   * `available` narrows when the report switches device (a table does not fit the
   * mobile grid), so a source the user had selected can disappear underneath them.
   * Falling back to `all` keeps the drawer showing something instead of stranding
   * it on an empty selection with no visible way out.
   */
  const activeFilter: WidgetPaletteFilter =
    filter !== 'all' && !availableGroups.some(( group ) => group.source === filter )
      ? 'all'
      : filter;

  const groups: WidgetSourceGroup[] = useMemo(() => {
    const query = search.trim().toLowerCase();
    return availableGroups
      .filter(( group ) => activeFilter === 'all' || group.source === activeFilter )
      .map(( group ) => ({
        source: group.source,
        types: group.types.filter(( type ) => matchesSearch( type, query, t ))
      }))
      .filter(( group ) => group.types.length > 0 );
    // `t` is in the deps because a language switch changes what the search matches.
  }, [ availableGroups, activeFilter, search, t ]);

  return (
    <>
      <CollapsedBar
        opened={opened}
        onOpen={() => setOpened( true )}
        widgets={available}
        onAddWidget={onAddWidget}
      />

      <PaletteDrawer
        opened={opened}
        onClose={() => setOpened( false )}
        sources={availableGroups.map(( group ) => group.source )}
        activeFilter={activeFilter}
        onFilterChange={setFilter}
        search={search}
        onSearch={setSearch}
        groups={groups}
        onAddWidget={( widgetDefinition ) => {
          onAddWidget( widgetDefinition );
          setOpened( false );
        }}
      />
    </>
  );
}
